import {
  OverviewTilePosition,
  OverviewTileType,
  Section,
} from 'app/frontend/types/custom'
import { overviewTileTypes } from 'app/frontend/types/custom/OverviewTileType'
import FileInput from './components/FileInput'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import RichTextField from 'app/frontend/base/components/form-fields/RichTextField'
import { FieldProps } from 'app/frontend/base/components/form-fields/types'
import NumberField from 'app/frontend/base/components/form-fields/NumberField'
import { z } from 'zod'
import { CATEGORIES } from './components/tiles/OverviewTableTile'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { SelectProps } from 'app/frontend/base/components/Select'
import {
  MAIN_OPTIONS,
  SIDEBAR_OPTIONS,
} from './components/tiles/RosterSummaryTile'
import CustomLinksField from './components/CustomLinksField'
import TileSelect from './components/TileSelect'
import CheckboxField from 'app/frontend/base/components/form-fields/CheckboxField'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import {
  ilvlDisplayOptions,
  slotDisplayOptions,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamAudit/column-definitions/gear_overview'
import {
  minimumQualityOptions,
  enchantViewOptions,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamAudit/column-definitions/enchantments'
import { minimumLevelOptions } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamAudit/column-definitions/dungeons_weekly'

export type ConfigField = {
  name: string
  Component: React.ComponentType<
    FieldProps<unknown> & { inModal?: boolean; configName?: string }
  >
  fieldProps?: Partial<
    FieldProps<unknown> & SelectProps & { position?: OverviewTilePosition }
  >
  default?: unknown
  type?: string
  validation?: z.ZodTypeAny
  position?: OverviewTilePosition
  showWhen?: { field: string; values: string[] }
}

type ConfigFieldsByTileType = Record<OverviewTileType, ConfigField[][]>

const asFieldComponent = <T>(Component: React.ComponentType<T>) =>
  Component as React.ComponentType<FieldProps<unknown>>

const emptyConfigFieldsByTileType = overviewTileTypes.reduce((acc, type) => {
  acc[type] = []
  return acc
}, {} as ConfigFieldsByTileType)

export const getConfigFieldsByTileType = (
  availableDifficulties: readonly Difficulty[],
  defaultDifficulty: Difficulty,
): ConfigFieldsByTileType => ({
  ...emptyConfigFieldsByTileType,
  'Tile::CustomLinks': [
    [
      {
        name: 'links',
        Component: asFieldComponent(CustomLinksField),
        default: [],
      },
    ],
  ],
  'Tile::CustomImage': [
    [{ name: 'url', Component: asFieldComponent(TextField), type: 'url' }],
    [{ name: 'image_url', Component: asFieldComponent(FileInput) }],
  ],
  'Tile::CustomText': [
    [
      {
        name: 'text',
        Component: asFieldComponent(RichTextField),
      },
    ],
  ],
  'Tile::EnchantAudit': [
    [
      {
        name: 'leniency',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: [
            { label: 'Count any from current expansion', value: 'lenient' },
            { label: 'Count only best-in-slot', value: 'strict' },
          ],
          transformOptions: false,
        },
        default: 'lenient',
      },
    ],
  ],
  'Tile::OverviewTable': [
    [
      {
        name: 'category',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: CATEGORIES,
          isClearable: false,
          transformOptions: false,
        },
        default: 'summary',
      },
      {
        name: 'weeks_ago',
        Component: asFieldComponent(NumberField),
        default: 0,
        validation: z.number().min(0).max(52),
        showWhen: {
          field: 'category',
          values: ['summary', 'vault', 'dungeons_weekly'],
        },
      },
    ],
    [
      {
        name: 'ilvl_display',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: ilvlDisplayOptions,
          isClearable: false,
          transformOptions: false,
        },
        default: 'best',
        showWhen: {
          field: 'category',
          values: ['summary', 'gear_overview'],
        },
      },
      {
        name: 'slot_display',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: slotDisplayOptions,
          isClearable: false,
          transformOptions: false,
        },
        default: 'ilvl',
        showWhen: {
          field: 'category',
          values: ['gear_overview'],
        },
      },
    ],
    [
      {
        name: 'minimum_quality',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: minimumQualityOptions,
          isClearable: false,
          transformOptions: false,
        },
        default: '4',
        showWhen: {
          field: 'category',
          values: ['enchantments', 'summary'],
        },
      },
      {
        name: 'enchant_view',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: enchantViewOptions,
          isClearable: false,
          transformOptions: false,
        },
        default: 'summary',
        showWhen: {
          field: 'category',
          values: ['enchantments'],
        },
      },
    ],
    [
      {
        name: 'raid_difficulty',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: availableDifficulties.map((diff) => ({
            label: difficultiesLabels[diff],
            value: diff,
          })),
          isClearable: false,
          transformOptions: false,
        },
        default: defaultDifficulty,
        showWhen: {
          field: 'category',
          values: ['raids_weekly', 'raids_overall'],
        },
      },
    ],
    [
      {
        name: 'minimum_level',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: minimumLevelOptions,
          isClearable: true,
          transformOptions: false,
        },
        showWhen: {
          field: 'category',
          values: ['dungeons_weekly', 'summary'],
        },
      },
    ],
  ],
  'Tile::PendingApplicants': [
    [
      {
        name: 'amount',
        Component: asFieldComponent(NumberField),
        default: 10,
        validation: z.number().min(1).max(20),
      },
      {
        name: 'empty_behavior',
        Component: asFieldComponent(SelectField),
        default: 'hide',
        fieldProps: {
          options: [
            { label: 'Always show', value: 'show' },
            { label: 'Hide when no applicants', value: 'hide' },
          ],
          isClearable: false,
          transformOptions: false,
        },
      },
    ],
  ],
  'Tile::RecentNews': [
    [
      {
        name: 'amount',
        Component: asFieldComponent(NumberField),
        default: 2,
        validation: z.number().min(1).max(20),
      },
    ],
    [
      {
        name: 'show_view_all',
        Component: asFieldComponent(CheckboxField),
        default: true,
        fieldProps: {
          layout: 'inline',
        },
      },
      {
        name: 'compact',
        Component: asFieldComponent(CheckboxField),
        default: true,
        fieldProps: {
          layout: 'inline',
        },
      },
    ],
  ],
  'Tile::RecentEvents': [
    [
      {
        name: 'amount',
        Component: asFieldComponent(NumberField),
        default: 5,
        validation: z.number().min(1).max(20),
      },
    ],
  ],
  'Tile::RosterSummary': [
    [
      {
        name: 'sidebar_summary_type',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: SIDEBAR_OPTIONS,
          transformOptions: false,
        },
        default: 'total_count',
        position: 'sidebar',
      },
    ],
    [
      {
        name: 'main_summary_type',
        Component: asFieldComponent(SelectField),
        fieldProps: {
          options: MAIN_OPTIONS,
          transformOptions: false,
        },
        default: 'stats',
        position: 'main',
      },
    ],
  ],
  'Tile::SidebarTiles': [
    Array.from({ length: 3 }).map((_, index) => ({
      name: `tile_${index + 1}`,
      Component: asFieldComponent(TileSelect),
      fieldProps: {
        position: 'sidebar',
        isClearable: index === 2,
      },
      validation:
        index === 2
          ? undefined
          : z
              .string({ required_error: 'Required' })
              .min(1, 'Required')
              .refine(
                (val) => overviewTileTypes.includes(val as OverviewTileType),
                { message: 'Required' },
              ),
    })),
  ],
  'Tile::UpcomingEvents': [
    [
      {
        name: 'amount',
        Component: asFieldComponent(NumberField),
        default: 5,
        validation: z.number().min(1).max(20),
      },
    ],
  ],
})

export const TILE_SECTION_MAP: Partial<Record<OverviewTileType, Section>> = {
  'Tile::PendingApplicants': 'applications',
  'Tile::UpcomingEvents': 'events',
  'Tile::RecentEvents': 'events',
  'Tile::RecentNews': 'news',
  'Tile::RosterSummary': 'roster',
  'Tile::EnchantAudit': 'roster',
}
